/**
 * The procedural test card of the effects comparer and its capture kit (W3-04): features every built-in effect
 * changes visibly, with no text and no randomness (the same pixels everywhere).
 *
 *  - top fifth: eight colour bars (white, yellow, cyan, green, magenta, red, blue, black): colour splits and greyscale;
 *  - a grey ramp, dark on the left: orientation, luma and vignette falloff;
 *  - a strip of 1 px (left half) and 2 px (right half) black and white stripes: blur, pixelation, jitter;
 *  - below: a dark field with a grid every 1/24 of the width, rings around the card's centre every 6 % of its
 *    height, and five small white dots (circle blur): distortions (fisheye, shockwave, radial blur, glitch bands);
 *  - an orange "F" in the lower-left corner: it reads differently under every flip and rotation.
 */

import type { RgbaImage } from './bench';

type Rgb = readonly [number, number, number];

const BARS: readonly Rgb[] = [
  [235, 235, 235],
  [235, 235, 16],
  [16, 235, 235],
  [16, 235, 16],
  [235, 16, 235],
  [235, 16, 16],
  [16, 16, 235],
  [16, 16, 16],
];
const FIELD: Rgb = [28, 35, 51];
const GRID: Rgb = [112, 128, 150];
const RING: Rgb = [240, 192, 64];
const DOT: Rgb = [255, 255, 255];
const MARK: Rgb = [255, 128, 32];

/** Card sizes: the comparer's default input, and the capture kit's line texture (fills a 16:9 Phira viewport). */
export const CARD_PREVIEW = { w: 960, h: 540 } as const;
export const CARD_KIT = { w: 1350, h: 760 } as const;

/** Whether (x, y) lies on the "F" marker: a 5 x 7 cell glyph in the lower-left corner. */
function onMarker(x: number, y: number, w: number, h: number): boolean {
  const cell = Math.max(2, Math.round(h * 0.02));
  const left = Math.round(w * 0.03);
  const top = h - Math.round(h * 0.04) - 7 * cell;
  const cx = Math.floor((x - left) / cell);
  const cy = Math.floor((y - top) / cell);
  if (x < left || y < top || cx > 4 || cy > 6) return false;
  return cx === 0 || cy === 0 || (cy === 3 && cx <= 3);
}

/** The test card at w x h (RGBA, opaque, top row first). */
export function testCard(w: number, h: number): RgbaImage {
  const W = Math.max(8, Math.round(w));
  const H = Math.max(8, Math.round(h));
  const data = new Uint8ClampedArray(W * H * 4);
  const barsEnd = Math.round(H * 0.2);
  const rampEnd = Math.round(H * 0.28);
  const stripesEnd = Math.round(H * 0.32);
  const gridStep = W / 24;
  const ringStep = H * 0.06;
  const cx = W / 2;
  const cy = H / 2;
  const dots: ReadonlyArray<readonly [number, number]> = [
    [W * 0.5, H * 0.66],
    [W * 0.2, H * 0.5],
    [W * 0.8, H * 0.5],
    [W * 0.35, H * 0.85],
    [W * 0.65, H * 0.85],
  ];
  const dotR = Math.max(1.5, H * 0.006);
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      let c: Rgb;
      if (y < barsEnd) c = BARS[Math.min(7, Math.floor((x * 8) / W))] ?? FIELD;
      else if (y < rampEnd) {
        const v = Math.round((x / (W - 1)) * 255);
        c = [v, v, v];
      } else if (y < stripesEnd) {
        const period = x < W / 2 ? 1 : 2;
        const on = Math.floor(x / period) % 2 === 0;
        c = on ? [255, 255, 255] : [0, 0, 0];
      } else {
        c = FIELD;
        const gx = x % gridStep;
        const gy = (y - stripesEnd) % gridStep;
        if (gx < 1 || gy < 1) c = GRID;
        const d = Math.hypot(x + 0.5 - cx, y + 0.5 - cy);
        if (d % ringStep < 2 && d > 1) c = RING;
        for (const [dx, dy] of dots) if (Math.hypot(x + 0.5 - dx, y + 0.5 - dy) <= dotR) c = DOT;
        if (onMarker(x, y, W, H)) c = MARK;
      }
      const i = (y * W + x) * 4;
      data[i] = c[0];
      data[i + 1] = c[1];
      data[i + 2] = c[2];
      data[i + 3] = 255;
    }
  }
  return { width: W, height: H, data };
}
